import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, Search, Wrench } from "lucide-react";
import DatePicker from "@/components/DatePicker";
import { toast } from "sonner";

// These run jobs: each is also a machine (Machines page) under the same code, for its run settings.
const MACHINE_TYPES = ["Press", "Flatbed", "AVT", "Slitter", "Rewinder", "Cutter"];
const TYPES = ["Press", "Slitter", "Die", "Anilox", "AVT", "Inspector", "Punch", "Flatbed", "Rewinder", "Cutter", "Other"];
const daysTo = (iso) => (iso ? Math.round((new Date(`${iso}T00:00:00`) - new Date(new Date().toDateString())) / 86400000) : null);

const blank = () => ({ name: "", equip_type: "Press", code: "", capacity: "", make: "", model: "", year: "", last_calibration: "", next_calibration: "", location: "", notes: "", active: true });

/**
 * Equipment master — presses, slitters, dies, aniloxes.
 * Operators can attach any of these to a stage entry so the shop-floor
 * traceability is complete.
 */
export default function Equipment() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("all");
  const [editing, setEditing] = useState(null);
  const [cal, setCal] = useState("all"); // all | overdue | soon | none

  const load = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const params = { limit: 1000 };
      if (q) params.q = q;
      if (typeF !== "all") params.equip_type = typeF;
      const { data } = await api.get("/equipment", { params });
      setRows(data);
    } catch (e) { if (!quiet) toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeF]);
  useLiveRefresh("masters", () => load({ quiet: true }));

  const del = async (id) => {
    if (!window.confirm("Delete this equipment?")) return;
    try { await api.delete(`/equipment/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const calOf = (r) => { const d = daysTo(r.next_calibration); return d == null ? "none" : d < 0 ? "overdue" : d <= 30 ? "soon" : "ok"; };
  const tracked = rows.filter((r) => r.active !== false);
  const counts = { overdue: tracked.filter((r) => calOf(r) === "overdue").length, soon: tracked.filter((r) => calOf(r) === "soon").length, none: tracked.filter((r) => calOf(r) === "none").length };
  const shown = cal === "all" ? rows : rows.filter((r) => r.active !== false && calOf(r) === cal);

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-neutral-200 px-8 py-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">Equipment Master</div>
        <h1 className="text-3xl font-black tracking-tight flex items-center gap-3"><Wrench size={22} className="text-[#FF5A00]" /> Machines &amp; Tooling</h1>
        <p className="text-sm text-neutral-500 mt-1">Every machine, tool and instrument, with make, location and calibration. Presses, AVT, slitters, rewinders, the flatbed and the core cutter are also the <Link to="/machines" className="underline">production machines</Link>, where capacity and hourly rate are set.</p>
      </div>
      <div className="p-8">
        <div className="border border-neutral-200 bg-white" data-testid="equipment-panel">
          <div className="px-4 py-3 border-b border-neutral-200 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input data-testid="equipment-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name / code / capacity / make" className="pl-8 h-9 rounded-sm" />
            </div>
            <Select value={typeF} onValueChange={setTypeF}>
              <SelectTrigger data-testid="equipment-type-filter" className="rounded-sm h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} className="rounded-sm h-9">Apply</Button>
            <div className="flex-1" />
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
              <DialogTrigger asChild>
                <Button data-testid="equipment-add-btn" onClick={() => setEditing(blank())} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] gap-1.5"><Plus size={13} /> Add Equipment</Button>
              </DialogTrigger>
              {editing && <EquipmentEditor row={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
            </Dialog>
          </div>

          <div className="px-4 py-2.5 border-b border-neutral-200 flex items-center gap-2 flex-wrap text-xs" data-testid="equipment-calibration-chips">
            <span className="label-caps">Calibration</span>
            {[["all", "All", rows.length, ""], ["overdue", "Overdue", counts.overdue, "text-rose-700 border-rose-200 bg-rose-50"], ["soon", "Due in 30 days", counts.soon, "text-amber-700 border-amber-200 bg-amber-50"], ["none", "No date entered", counts.none, ""]].map(([k, label, n, tone]) => (
              <button key={k} type="button" onClick={() => setCal(k)} data-testid={`equipment-cal-${k}`}
                className={`px-2 py-1 rounded-sm border ${cal === k ? "bg-neutral-900 text-white border-neutral-900" : tone || "border-neutral-200 text-neutral-700"}`}>
                {label} · {n}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="equipment-table">
              <thead className="bg-neutral-50 label-caps text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-left px-3 py-2.5">Name</th>
                  <th className="text-left px-3 py-2.5">Code</th>
                  <th className="text-left px-3 py-2.5">Capacity</th>
                  <th className="text-left px-3 py-2.5">Make / Model</th>
                  <th className="text-left px-3 py-2.5">Last Calibration</th>
                  <th className="text-left px-3 py-2.5">Next Calibration</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="text-center py-8 text-neutral-400">Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-neutral-400" data-testid="equipment-empty">No equipment yet — click Add Equipment to create one.</td></tr>}
                {!loading && rows.length > 0 && shown.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-neutral-400">Nothing in this group.</td></tr>}
                {!loading && shown.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100 data-row" data-testid={`equipment-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest border border-neutral-200 bg-neutral-50">{r.equip_type}</span>
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {r.name}
                      {MACHINE_TYPES.includes(r.equip_type) && r.code && (
                        <Link to="/machines" className="ml-2 text-[10px] uppercase tracking-widest text-[#FF5A00] hover:underline" data-testid={`equipment-machine-${r.id}`}>machine</Link>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono-num text-xs">{r.code || "—"}</td>
                    <td className="px-3 py-2 text-xs">{r.capacity || "—"}</td>
                    <td className="px-3 py-2 text-xs">{[r.make, r.model].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="px-3 py-2 font-mono-num text-xs">{r.last_calibration || "—"}</td>
                    <td className="px-3 py-2 font-mono-num text-xs">
                      {r.next_calibration ? (
                        <span className="inline-flex items-center gap-2 flex-wrap">
                          {r.next_calibration}
                          {r.active !== false && calOf(r) === "overdue" && <span className="whitespace-nowrap text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-rose-200 text-rose-700 bg-rose-50" data-testid={`equipment-cal-badge-${r.id}`}>Overdue {-daysTo(r.next_calibration)}d</span>}
                          {r.active !== false && calOf(r) === "soon" && <span className="whitespace-nowrap text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-amber-200 text-amber-700 bg-amber-50" data-testid={`equipment-cal-badge-${r.id}`}>{daysTo(r.next_calibration) === 0 ? "Due today" : `Due in ${daysTo(r.next_calibration)}d`}</span>}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${r.active ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-neutral-200 text-neutral-600 bg-neutral-50"}`}>
                        {r.active ? "Active" : "Archived"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="icon" data-testid={`equipment-edit-${r.id}`} onClick={() => setEditing(r)} className="h-7 w-7"><Pencil size={13} /></Button>
                        <Button variant="ghost" size="icon" data-testid={`equipment-del-${r.id}`} onClick={() => del(r.id)} className="h-7 w-7 text-rose-600"><Trash2 size={13} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function EquipmentEditor({ row, onSaved, onCancel }) {
  const [f, setF] = useState({ ...row });
  const [saving, setSaving] = useState(false);
  const isEdit = !!row.id;

  const save = async () => {
    if (!f.name || !f.equip_type) { toast.error("Name and Type are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        year: f.year === "" || f.year == null ? null : Number(f.year),
      };
      if (isEdit) await api.patch(`/equipment/${row.id}`, payload);
      else await api.post("/equipment", payload);
      toast.success(isEdit ? "Equipment updated" : "Equipment added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit" : "New"} Equipment</DialogTitle>
        <DialogDescription>Track the make, capacity and calibration dates so operators pick the right equipment for each stage.</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <F label="Name *" testid="eq-name" value={f.name} onChange={(v) => setF({ ...f, name: v })} full />
        <div>
          <label className="label-caps block mb-1 text-[10px]">Type *</label>
          <Select value={f.equip_type} onValueChange={(v) => setF({ ...f, equip_type: v })}>
            <SelectTrigger data-testid="eq-type" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <F label="Code / Asset Tag" testid="eq-code" value={f.code} onChange={(v) => setF({ ...f, code: v })} />
        <F label="Capacity" testid="eq-capacity" value={f.capacity} onChange={(v) => setF({ ...f, capacity: v })} full />
        <F label="Make" testid="eq-make" value={f.make} onChange={(v) => setF({ ...f, make: v })} />
        <F label="Model" testid="eq-model" value={f.model} onChange={(v) => setF({ ...f, model: v })} />
        <F label="Year" testid="eq-year" type="number" value={f.year ?? ""} onChange={(v) => setF({ ...f, year: v })} />
        <div>
          <label className="label-caps block mb-1 text-[10px]">Last Calibration</label>
          <DatePicker value={f.last_calibration} onChange={(v) => setF({ ...f, last_calibration: v })} testId="eq-last-cal" />
        </div>
        <div>
          <label className="label-caps block mb-1 text-[10px]">Next Calibration</label>
          <DatePicker value={f.next_calibration} onChange={(v) => setF({ ...f, next_calibration: v })} testId="eq-next-cal" />
        </div>
        <F label="Location" testid="eq-loc" value={f.location} onChange={(v) => setF({ ...f, location: v })} />
        <F label="Notes" testid="eq-notes" value={f.notes} onChange={(v) => setF({ ...f, notes: v })} full />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} data-testid="eq-cancel">Cancel</Button>
        <Button disabled={saving} onClick={save} data-testid="eq-save" className="bg-[#FF5A00] hover:bg-[#e04f00]">{saving ? "Saving…" : "Save"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function F({ label, value, onChange, testid, type = "text", full }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      <label className="label-caps block mb-1 text-[10px]">{label}</label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm h-9 text-sm" />
    </div>
  );
}
